import Config from "./Config";
import BaseService from './BaseService';
import AppStorage from "../storage/AppStorage";
import UpdateService from "./UpdateService";
import UrlService from "./UrlService";
import UrlParamsService from "./UrlParamsService";
import AnalyticsAPIService from "./AnalyticsAPIService";
import { Device } from '@capacitor/device';
import type { DeviceInfo } from '@capacitor/device';
import type { ReactNode } from 'react';
import { Share } from '@capacitor/share';
import VisibilityService from "./VisibilityService";
import i18n from "../i18n";

declare global {
    interface Navigator {
        /** Solo en Safari iOS: true cuando la PWA se abre desde el icono. */
        standalone?: boolean;
    }
}

/**
 * Evento no estandar de Chromium que precede a la instalacion de la PWA.
 *
 * No esta en WindowEventMap, asi que addEventListener lo entrega como un Event
 * generico y hay que estrecharlo en el punto de escucha.
 */
export interface BeforeInstallPromptEvent extends Event {
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * Lo que los servicios usan de la instancia del componente App.
 *
 * Es un tipo estructural a proposito, no el de la clase App: los servicios los
 * importa App, asi que referenciar el componente aqui cerraria el ciclo.
 */
export interface AppComponentRef {
    statusRequestCallback(isLoading: boolean): void;
    closeMenu(open?: boolean): void;
    closeAllOverlays(): void;
    closeTopOverlay(): boolean;
    handleOpenPage: (component: ReactNode) => Promise<void>;
    handleClosePage: () => void;
}

/**
 * Helper for App lifecycle management.
 */
class AppService extends BaseService {

    // device info returned from capacitor
    static deviceInfo: DeviceInfo;

    // Redirecting, wait until reload;
    static redirecting = false;

    /**
     * Define storage class
     */
    static storage = AppStorage;

    /**
     * offline state of the device
     */
    static offline = true;

    /**
     * Reference to the App component instance
     */
    static appcomponent: AppComponentRef;

    /**
     * store if device can share
     */
    static canShare: boolean | undefined;

    /**
     * Gets App name + version info.
     */
    static getAppInfoLabel(): string {
        return [Config.getAppName(), Config.getAppTag(), Config.getAppVersion()].join(' ');
    }

    /**
     * Gets formatted device info.
     */
    static getDeviceInfoQueryString(): string {
        const deviceInfo = {
            platform: this.deviceInfo.platform,
            device: this.deviceInfo.model,
            version: Config.getAppVersion(),
            lang: i18n.language,
        };
        const deviceQuery = Object.entries(deviceInfo)
            .filter(([, value]) => value != null && value !== '')
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return deviceQuery;
    }

    /**
     * Check whether App is installed.
     */
    static isInstalled(): boolean {
        return !!(this.storage.getInstallDate() || this.isStandAlone());
    }

    /**
     * Gets install information for debug.
     */
    static getInstallInfo(): string {
        return this.isInstalled() ?
            ['Installed', AppService.storage.getInstalledVersion(), AppService.storage.getInstallDate()].join(' ') :
            'Not Installed'
            ;
    }

    /**
     * Check app running standalone mode (is run as installed PWA or native).
     *
     * @see https://stackoverflow.com/questions/66009755/how-to-detect-if-pwa-is-already-installed-on-ios
     * @see window.matchMedia('(display-mode: standalone)').matches
     */
    static isStandAlone(): boolean {
        // native app version
        if(this.isNativeApp()) {
            return true;
        }
        // web / pwa version
        if ('standalone' in window.navigator) {
            // For iOS Only
            return !!window.navigator.standalone;
        } else {
            // Android
            return window.matchMedia('(display-mode: standalone)').matches;
        }
    }

    /**
     * Check app running native mode.
     */
    static isNativeApp(): boolean {
        return this.deviceInfo.platform === this.deviceInfo.operatingSystem;
    }

    /**
     * Detects if device is on iOS
     *
     * @see https://stackoverflow.com/questions/50543163/can-i-detect-if-my-pwa-is-launched-as-an-app-or-visited-as-a-website
     */
    static isIos(): boolean {
        return this.deviceInfo.operatingSystem === 'ios';
    }

    /**
     * Detects if device is on Android
     */
    static isAndroid(): boolean {
        return this.deviceInfo.operatingSystem === 'android';
    }

    /**
     * Detects if is running as PWA installed or not
     *
     */
    static isPwa(): boolean {
        return this.deviceInfo.platform === 'web';
    }

    /**
     * Whether App needs user login.
     */
    static needsUserLogin(): boolean {
        return Config.isLoginRequired();
    }

    /**
     * Whether App must show User account / data.
     */
    static showUserAccount(): boolean {
        return Config.isLoginAllowed();
    }

    /**
     * Gets runtime device information.
     */
    static getDeviceInfo(): string {
        return [
            this.isIos() ? 'ios' : '',
            this.isPwa() ? 'pwa' : '',
            this.isStandAlone() ? 'standalone' : '',
            this.isNativeApp() ? 'native' : '',
        ].join(' ');
    }


    /**
     * Gets debug information.
     */
    static getDebugInfo(): string {
        return [Config.getAppEnv(), Config.getBuildEnv(), this.getDeviceInfo(), this.getInstallInfo(), UpdateService.getDebugInfo()].join(' ');
    }

    /**
     * Set redirecting state.
     */
    static setRedirecting(status = true): void {
        this.redirecting = status;
    }

    /**
     * Check whether app is redirecting.
     */
    static isRedirecting(): boolean {
        return this.redirecting;
    }

    /**
     * Mark App as installed / not installed.
     */
    static setInstalled(isInstalled = true): void {
        this.info("AppService.setInstalled", isInstalled);
        const version = Config.getAppVersion();
        if (isInstalled) {
            this.storage.setInstallDate();
            this.storage.setInstalledVersion(version);
            AnalyticsAPIService.sendEvent('App', 'Installed');
        }
        else {
            this.storage.clear();
        }
    }

    /**
     * Event callback: 'appinstalled'
     */
    static eventAppInstalled(): void {
        this.info('AppService.eventAppInstalled');
        this.setInstalled();
    }

    /**
     * Event callback: 'beforeinstallprompt'
     */
    static eventBeforeInstallPrompt(event: BeforeInstallPromptEvent): void {
        this.info('AppService.eventBeforeInstallPrompt');
        // this.setInstalled(false);

        event.userChoice.then(choice => {
            this.info('AppService.eventBeforeInstallPrompt/choice', choice);
            AnalyticsAPIService.sendEvent('App', 'Add to home', choice.outcome);
        });

    }

    /**
     * Add device classes to body
     *
     * @see index.js
     */
    static setModelClass(): void {
        document.body.classList.add(this.deviceInfo.platform);
        let deviceModel = this.deviceInfo.model.replace(/\s/g, '_');
        let notch = '';

        const noNotchModels = [
            'iPhone10,1','iPhone10,2','iPhone10,4','iPhone10,5', // iPhone 8 / 8 Plus
            'iPhone12,8', // iPhone SE (2020)
            'iPhone14,6', // iPhone SE (2022)
        ];

        if (deviceModel.indexOf('iPhone') !== -1 ||
            (this.deviceInfo.platform === 'android' && parseFloat(this.deviceInfo.osVersion) > 14)) {
            deviceModel = this.deviceInfo.platform === 'android' ? 'android' : 'iphone';
            notch = (this.deviceInfo.platform === 'android' && parseFloat(this.deviceInfo.osVersion) > 14) ||
            (this.deviceInfo.platform === 'ios' &&
                parseFloat(this.deviceInfo.model.substring(6).replace(',', '.')) >= 10 &&
                noNotchModels.indexOf(this.deviceInfo.model) === -1
            )
                ? 'notch' :  '';
            if (notch !== '') document.body.classList.add(notch);
        }

        document.body.classList.add(deviceModel);
    }

    /**
     * Check whether app is opened for first time.
     */
    static isFirstTime(): boolean {
        if (AppStorage.getOpenedBefore())
            return false;

        return true;
    }

    /**
     * Initialize App.
     *
     * @see index.js
     */
    static async init(): Promise<boolean> {
        try {
            this.deviceInfo = await Device.getInfo();
            this.debug("AppService.deviceInfo", this.deviceInfo);

            this.setModelClass();

            const initStatus = {
                isInstalled: this.isInstalled(),
                isStandAlone: this.isStandAlone(),
                isIos: this.isIos()
            };

            this.info("AppService.init", initStatus);

            // Look up type / id params in the URL.
            await UrlParamsService.init();

            // The city list is NOT loaded here: this runs before any
            // authentication (client authentication happens later, in
            // App.initApp), so on a first run the fetch has no auth data and
            // fails, aborting the rest of this init. It is only needed by the
            // city search, so App loads it in the background once authenticated.
            // @see App.loadCityList
            UpdateService.init();

            // Initialize visibility service from storage
            VisibilityService.init();

            // Track App Installs
            // @see https://www.billerickson.net/code/track-pwa-install-in-google-analytics/
            window.addEventListener('appinstalled', () => {
                AppService.eventAppInstalled();
            });

            window.addEventListener('beforeinstallprompt', (event) => {
                AppService.eventBeforeInstallPrompt(event as BeforeInstallPromptEvent);
            });

            this.canShare = this.isNativeApp() && (await Share.canShare()).value;

            this.debug("Succcessful init.");
            return true;
        } catch (e) {
            this.debug("Failed init", e);
            return false;
        }
    }

    /**
     * Set the reference of the app instance. We use this to call diferente handlers from eny component or service
     */
    static setAppComponent(instance: AppComponentRef): void {
        this.appcomponent = instance;
    }

    /**
     * Initialize App // Second stage
     *
     * @see index.js
     */
    static async initAll(): Promise<boolean> {
        // If we are redirecting somewhere else, like for Oauth callbacks, prevent further initialization.
        if (this.isRedirecting()) return false;
        AnalyticsAPIService.init();
        UrlService.init();

        // Send first pageview event.
        AnalyticsAPIService.sendPageView(UrlService.getPath());

        return true;
    }

    /**
     * get the needed event type for the app type and SO
     */
    static getInteractiveEventType(): 'touchend' | 'click' {
        return (this.isNativeApp() && this.isIos()) ? 'touchend' : 'click';
    }

    /**
     * show/hide the loader distractor on App component
     */
    static setLoadingState(value: boolean): void {
        this.appcomponent.statusRequestCallback(value);
    }

    /**
     * returns true if the device is in portrait mode
     */
    static isPortrait(): boolean {
        return window.matchMedia("(orientation: portrait)").matches;
    }

    /**
     * returns true if the device is in landscape mode
     */
    static isLandscape(): boolean {
        return window.matchMedia("(orientation: landscape)").matches;
    }

}

export default AppService;
